'use client';

import { base64ToString, stringToBase64 } from '@websolutespa/bom-core';
import { useRouter } from 'next/compat/router';
import { NextRouter } from 'next/router';
import { useCallback } from 'react';

const USE_ENCRYPTION = true;

function stringToURLSearchParams(url: string): URLSearchParams {
  // console.log('stringToURLSearchParams', url);
  return new URLSearchParams(url);
}

/*
function hasURLSearchParams(url: string, key: string):boolean {
  const params = stringToURLSearchParams(url);
  return params.has(key);
}
*/

function getURLSearchParams(url: string, key: string): string | null {
  const params = stringToURLSearchParams(url);
  return params.get(key);
}

function setURLSearchParams(url: string, keyOrValue: string, value: string | null | undefined): URLSearchParams { // !!! any
  const params = stringToURLSearchParams(url);
  if (value != null) {
    params.set(keyOrValue, value);
  } else {
    params.set(keyOrValue, '');
  }
  return params;
  // push_(params);
  // historyReplaceState(params);
}

function deserialize_(url: string, key?: string) {
  const encoded = getURLSearchParams(url, 'params');
  return decode(encoded, key);
}

function serialize_(url: string, value: IParamsEncoded, key?: string): URLSearchParams {
  const params = deserialize_(url);
  const encoded = encode(params, value, key);
  return setURLSearchParams(url, 'params', encoded);
}

function decode(encoded: string | null | undefined, key?: string): IParamsEncoded {
  let decoded = null;
  if (encoded) {
    const json = USE_ENCRYPTION ? base64ToString(encoded) : encoded;
    decoded = JSON.parse(json);
  }
  // console.log('useSearchParamsEncoded.decode', decoded);
  if (key && decoded) {
    decoded = decoded[key];
  }
  return decoded || null;
}

function encode(params: IParamsEncoded, value: IParamsEncoded, key?: string) {
  params = params || {};
  let encoded = null;
  if (typeof key === 'string') {
    params[key] = value;
  } else if (typeof value === 'object') {
    params = Object.assign(params, value);
  }
  // console.log('useSearchParamsEncoded.encode', params);
  const json = JSON.stringify(params);
  encoded = USE_ENCRYPTION ? stringToBase64(json) : json;
  return encoded;
}

function searchParamsToObject_(params: URLSearchParams): { [key: string]: string } {
  const entries = params.entries();
  const result: { [key: string]: string } = {};
  for (const [key, value] of entries) { // each 'entry' is a [key, value] tuple
    result[key] = value;
  }
  return result;
}

export function getSearchParamsEncoded(url: string, key?: string): {} | null {
  const components = url.split('?');
  const search = components[1] || '';
  const searchParams = new URLSearchParams(search);
  const params = searchParams.get('params');
  const value = params ? decode(params, key) : null;
  // console.log('getSearchParamsEncoded', value, params, search);
  return value;
}

export function updateSearchParamsEncoded(currentPath: string, value: IParamsEncoded, key?: string): { pathname: string, query: { [key: string]: string } } {
  const components = currentPath.split('?');
  const searchParams = serialize_(components[1] || '', value, key);
  const query = searchParamsToObject_(searchParams);
  const pathname = components[0] || '';
  // console.log(pathname, query);
  return { pathname, query };
}

export function pushSearchParamsEncoded(router: NextRouter, value: IParamsEncoded, key?: string): void {
  const { pathname, query } = updateSearchParamsEncoded(router?.asPath, value, key);
  router?.push({ pathname, query });
}

export function pushSearchParamsEncodedSilently(router: NextRouter, value: IParamsEncoded, key?: string): void {
  const { pathname, query } = updateSearchParamsEncoded(router?.asPath, value, key);
  router?.push(pathname, { pathname, query }, { shallow: true, scroll: false });
}

export function replaceSearchParamsEncoded(router: NextRouter, value: IParamsEncoded, key?: string): void {
  const { pathname, query } = updateSearchParamsEncoded(router?.asPath, value, key);
  router?.replace({ pathname, query });
}

export function replaceSearchParamsEncodedSilently(router: NextRouter, value: IParamsEncoded, key?: string): void {
  const { pathname, query } = updateSearchParamsEncoded(router?.asPath, value, key);
  router?.replace(pathname, { pathname, query }, { shallow: true, scroll: false });
}

export type IParamsEncoded = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
} | null;

export type IUseSearchParamsEncodedResult = {
  params: IParamsEncoded;
  pushParams: (params: IParamsEncoded) => void;
  pushParamsSilently: (params: IParamsEncoded) => void;
  replaceParams: (params: IParamsEncoded) => void;
  replaceParamsSilently: (params: IParamsEncoded) => void;
};

export function useSearchParamsEncoded(key?: string): IUseSearchParamsEncodedResult {
  const router = useRouter();
  const params = router ? getSearchParamsEncoded(router?.asPath, key) : {};

  const pushParams = useCallback((params: IParamsEncoded) => {
    if (router) {
      pushSearchParamsEncoded(router, params, key);
    }
  }, [key, router]);

  const pushParamsSilently = useCallback((params: IParamsEncoded) => {
    if (router) {
      pushSearchParamsEncodedSilently(router, params, key);
    }
  }, [key, router]);

  const replaceParams = useCallback((params: IParamsEncoded) => {
    if (router) {
      replaceSearchParamsEncoded(router, params, key);
    }
  }, [key, router]);

  const replaceParamsSilently = useCallback((params: IParamsEncoded) => {
    if (router) {
      replaceSearchParamsEncodedSilently(router, params, key);
    }
  }, [key, router]);

  return { params, pushParams, pushParamsSilently, replaceParams, replaceParamsSilently };
}
